import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { VIEW_MODE_MAP, PERMISSION_VIEW_MAP } from '@/config/routeConfig';
import { AI_ASSISTANT_PERMISSION, normalizePermission } from '@/hooks/use-auth';
import { normalizeRole } from '@/hooks/use-role';
import {
  getAllowedPortfolioCompanyIds,
  getScopedErrorPath,
} from '@/utils/route-context';
import { openNotificationWithIcon } from '@components/help';
import { LINK_ACCESS_DENIED_MESSAGE } from '@/utils/deep-link';

const LinkAccessDeniedRedirect = () => {
  useEffect(() => {
    openNotificationWithIcon('error', LINK_ACCESS_DENIED_MESSAGE);
  }, []);

  return <Navigate to="/switch-view" replace />;
};

const RouteGuard = ({ children, requiredPermission, viewType }) => {
  const params = useParams();
  const viewMode = useSelector(state => state.App.viewMode);
  const permissionValue = useSelector(state => state.Auth.permission);
  const roleValue = useSelector(state => state.Auth.role);
  const isSuperuserFlag = useSelector(state => state.Auth.is_superuser);
  const userFacilities = useSelector(state => state.Auth.user_facilities);
  const permissions = normalizePermission(permissionValue);
  const role = normalizeRole(roleValue);
  const isSuperUser = role === 1 || Boolean(isSuperuserFlag);
  const currentViewType = VIEW_MODE_MAP[viewMode];
  const context = {
    companyId: params.companyId,
    facilityId: params.facilityId,
  };

  const getInternalErrorPath = (errorType) => getScopedErrorPath(viewType, errorType, context);

  if (role === 5 && viewType !== 'tennant') {
    return <Navigate to="/tennant/service-request" replace />;
  }

  if (viewType === 'common') {
    return children;
  }

  if (viewType === 'tennant') {
    if (role !== 5) {
      return <Navigate to="/403" replace />;
    }
    if (currentViewType !== 'tennant') {
      return <Navigate to="/tennant/service-request" replace />;
    }
    return children;
  }

  if (viewType === 'facility') {
    if (!Array.isArray(userFacilities)) {
      return children;
    }

    const facilities = Array.isArray(userFacilities) ? userFacilities : [];
    const ownsFacilityContext = facilities.some(
      (facility) =>
        String(facility.id) === String(params.facilityId) &&
        String(facility.company_id) === String(params.companyId)
    );

    if (!ownsFacilityContext) {
      return <LinkAccessDeniedRedirect />;
    }
  }

  if (viewType === 'portfolio') {
    if (!Array.isArray(userFacilities)) {
      return children;
    }

    const allowedCompanyIds = getAllowedPortfolioCompanyIds({ user_facilities: userFacilities });
    const ownsCompanyContext = allowedCompanyIds.includes(String(params.companyId));

    if (!ownsCompanyContext) {
      return <LinkAccessDeniedRedirect />;
    }
  }

  // AI Assistant is profile-driven only. The `isSuperUser` bypass below would
  // otherwise open the AI routes for a super admin whose profile lacks
  // `facility_ai_chat`, so that single permission is checked first.
  if (
    requiredPermission === AI_ASSISTANT_PERMISSION &&
    !((permissions[requiredPermission] || 0) > 0)
  ) {
    if (viewType === 'facility' || viewType === 'portfolio') {
      return <LinkAccessDeniedRedirect />;
    }
    return <Navigate to={getInternalErrorPath('403')} replace />;
  }

  if (isSuperUser) {
    return children;
  }

  const isRouteMatchingView = viewType === currentViewType;

  if (!isRouteMatchingView) {
    return <Navigate to={getInternalErrorPath('404')} replace />;
  }

  const viewPermissionKey = PERMISSION_VIEW_MAP[viewType];
  const hasViewPermission = (permissions[viewPermissionKey] || 0) > 0;

  if (!hasViewPermission) {
    if (viewType === 'facility' || viewType === 'portfolio') {
      return <LinkAccessDeniedRedirect />;
    }
    return <Navigate to={getInternalErrorPath('403')} replace />;
  }

  if (requiredPermission) {
    const hasPermission = (permissions[requiredPermission] || 0) > 0;
    if (!hasPermission) {
      if (viewType === 'facility' || viewType === 'portfolio') {
        return <LinkAccessDeniedRedirect />;
      }
      return <Navigate to={getInternalErrorPath('403')} replace />;
    }
  }

  return children;
};

export default RouteGuard;
